import { useCallback, useSyncExternalStore } from "react";

import type { TargetCostSetting } from "@/lib/spec-lines-display";

export { targetCostSwitchSetting as editorSwitchSetting } from "@/lib/spec-lines-display";
export type { TargetCostSetting };

// The target-cost switch's two settings (#1216, #1241 D1).
//
// The donor keeps one state per **page kind** — `specifications.showTargetCost`
// for the details page (`specificationsSlice.ts:6`) and
// `specEditIsland.showTargetCost` for the editor (`SpecEditIsland/data/slice.ts
// :17`) — so a switch-off on a details page left the community editor's on,
// and its toast came back. The owner ruled one setting per visit instead
// (#1241 D1, option c, a named break): **target cost**, which the details
// page's switch and a community spec's editor's share, so a switch-off
// anywhere stays off everywhere until a reload; and **max price**, a base or
// core spec's editor's "Show Max Price", which hides the Item Search cards'
// Max Price chips and stays its own setting (asked 2026-11-02). The editor
// picks its setting by the rule that picks its label
// (`targetCostSwitchSetting`, `targetCostSwitchLabel`). Both are on at boot (#1165 Q17).
//
// **A module store, browser-session wide.** The donor's slices are global: a
// choice made on one spec carries to the next, survives every in-app move
// (details ↔ edit, the list and back), and only a reload resets it. A context
// under a layout route would reset when that route unmounts, so the state
// lives here, outside React, read through `useSyncExternalStore`.
//
// **Nothing writes it on the server.** Its server snapshot is the boot value,
// `true`, whatever the browser holds, and the only writer is a switch's change
// handler, which runs in the browser — so no request can see another's state.
//
// The public share page reads neither (#1241 D2): it shows no target cost.

type SwitchState = Readonly<Record<TargetCostSetting, boolean>>;

/** Both settings' value on a fresh visit (#1165 Q17) — and the share page's
 *  fixed value, which never reads the in-app one (#1241 D2). */
export const TARGET_COST_SWITCH_AT_BOOT = true;

const BOOT: SwitchState = {
  targetCost: TARGET_COST_SWITCH_AT_BOOT,
  maxPrice: TARGET_COST_SWITCH_AT_BOOT,
};

let state: SwitchState = BOOT;
const listeners = new Set<() => void>();

export function readTargetCostSwitch(setting: TargetCostSetting): boolean {
  return state[setting];
}

export function setTargetCostSwitch(setting: TargetCostSetting, on: boolean) {
  if (state[setting] === on) return;
  state = { ...state, [setting]: on };
  for (const listener of listeners) listener();
}

export function subscribeTargetCostSwitch(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Both settings back on, as a reload leaves them — between test cases. */
export function resetTargetCostSwitchForTest() {
  state = BOOT;
  for (const listener of listeners) listener();
}

/**
 * One setting's switch: its state, and the writer its change handler calls.
 */
export function useTargetCostSwitch(
  setting: TargetCostSetting,
): [on: boolean, setOn: (on: boolean) => void] {
  const on = useSyncExternalStore(
    subscribeTargetCostSwitch,
    () => readTargetCostSwitch(setting),
    () => BOOT[setting],
  );
  const setOn = useCallback((next: boolean) => setTargetCostSwitch(setting, next), [setting]);
  return [on, setOn];
}
